import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/store';
import { FuseFlatNavItemType, FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import FuseNavigationHelper from '@fuse/utils/FuseNavigationHelper';
import { getAllowedRoutes } from '@auth/authRoles';

const navigationAdapter = createEntityAdapter<FuseFlatNavItemType>();

const emptyInitialState = navigationAdapter.getInitialState([]);

// Start with empty state - NavigationLoader will populate it with filtered navigation
const initialState = emptyInitialState;

export const {
	selectAll: selectNavigationAll,
	selectIds: selectNavigationIds,
	selectById: selectNavigationItemById
} = navigationAdapter.getSelectors<RootState>((state) => state.navigation || emptyInitialState);

const filterNavigationByRole = (navigation: FuseNavItemType[], role: string, hasPlan = true): FuseNavItemType[] => {
	const allowedPaths = getAllowedRoutes(role, hasPlan);

	// For non-applicant users, don't check for plan - just filter by role
	if (allowedPaths.includes('*')) {
		return navigation;
	}

	// Filter navigation items by allowed paths
	const filtered = navigation
		.map((item) => {
			// Check if the item URL is in allowed paths
			const itemUrl = item.url || '';
			// Check if item URL exactly matches an allowed path, or if an allowed path starts with item URL
			const itemIsAllowed = allowedPaths.some((path) => itemUrl === path || path.startsWith(itemUrl + '/'));
			// If item has children, filter them first
			let filteredChildren: FuseNavItemType[] | undefined;

			if (item.children && item.children.length > 0) {
				filteredChildren = item.children.filter((child) => {
					const childUrl = child.url || '';
					// Check if child URL exactly matches an allowed path
					const childIsAllowed = allowedPaths.some((path) => childUrl === path);
					return childIsAllowed;
				});
			}

			// Include item if:
			// 1. The item itself is allowed, OR
			// 2. The item has allowed children (for collapse items)
			const hasAllowedChildren = filteredChildren && filteredChildren.length > 0;
			const shouldInclude = itemIsAllowed || hasAllowedChildren;

			if (!shouldInclude) {
				return null;
			}

			return {
				...item,
				...(filteredChildren ? { children: filteredChildren } : {})
			};
		})
		.filter((item): item is FuseNavItemType => item !== null);

	return filtered;
};

/**
 * The navigation slice
 */
export const navigationSlice = createSlice({
	name: 'navigation',
	initialState,
	reducers: {
		setNavigation(state, action: PayloadAction<{ items: FuseNavItemType[]; role: string; hasPlan?: boolean }>) {
			const { items, role, hasPlan = true } = action.payload;
			const filteredNavigation = filterNavigationByRole(items, role, hasPlan);
			return navigationAdapter.setAll(state, FuseNavigationHelper.flattenNavigation(filteredNavigation));
		},
		resetNavigation: () => initialState
	}
});

export const { setNavigation, resetNavigation } = navigationSlice.actions;

export type navigationSliceType = typeof navigationSlice;

export default navigationSlice.reducer;
